/**
 * Custom hook for managing recurring transactions
 *
 * Provides:
 * - CRUD operations for recurring transactions
 * - Real-time subscriptions via Supabase
 * - Optimistic updates for better UX
 * - Date calculation utilities
 * - Filtering and search capabilities
 */

import { useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import type { Transaction, TransactionFormData, RecurrencePattern } from '@/types/transaction';
import { useTransaction } from './useTransaction';
import {
  calculateNextRecurrenceDate,
  generateRecurrenceDescription,
  isValidRecurrencePattern,
  calculateRecurrenceCount
} from '@/lib/dateUtils';
import { getRecurringGenerationLocks } from './recurringGenerationLocks';
import { fromDateString } from '@/utils/dateUtils';
import { generateRecurringTransactionsForSeries } from '@/services/recurring';

// Types for recurring transaction operations
export interface RecurringTransaction extends Transaction {
  is_recurring: true;
  recurrence_pattern: RecurrencePattern;
  recurrence_interval: number;
  recurrence_count?: number;
  recurrence_created_count: number;
  recurrence_end_date?: Date | string | null;
  next_occurrence?: Date | null;
  children_count?: number;
}

export interface RecurringTransactionFilters {
  search?: string;
  type?: 'income' | 'expense' | 'all';
  pattern?: RecurrencePattern | 'all';
  status?: 'active' | 'paused' | 'completed';
  category_id?: number;
  date_range?: {
    start: Date;
    end: Date;
  };
}

export interface RecurringTransactionFormData extends TransactionFormData {
  is_recurring: true;
  recurrence_pattern: RecurrencePattern;
  recurrence_interval: number;
  recurrence_count?: number;
  recurrence_end_date?: Date | string | null;
}

const MAX_RECURRENCE_COUNT = 100;
const MAX_ESTIMATED_OCCURRENCES = 1000;

const parseDateValue = (value?: Date | string | null) => {
  if (!value) return null;
  return value instanceof Date ? value : new Date(value);
};

export function useRecurringTransactions(filters?: RecurringTransactionFilters) {
  const queryClient = useQueryClient();
  const { create, update, remove } = useTransaction();
  const generationLocks = getRecurringGenerationLocks();

  // Fetch recurring transactions with filters
  const { data: recurringTransactions = [], isLoading, error } = useQuery({
    queryKey: ['recurring-transactions', filters],
    queryFn: async () => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      let query = supabase
        .from('transactions')
        .select(`
          *,
          categories (
            id,
            name
          ),
          clients:client_id (
            id,
            name
          ),
          suppliers:supplier_id (
            id,
            name
          )
        `)
        .eq('user_id', user.id)
        .eq('is_recurring', true);

      // Apply filters (search is applied client-side)
      // Note: search filter is intentionally NOT applied here to avoid re-fetching on each keystroke
      // It will be applied in the component after data is returned

      if (filters?.type && filters.type !== 'all') {
        query = query.eq('type', filters.type);
      }

      if (filters?.pattern && filters.pattern !== 'all') {
        query = query.eq('recurrence_pattern', filters.pattern);
      }

      if (filters?.status) {
        switch (filters.status) {
          case 'active':
            query = query.or(`recurrence_count.is.null,recurrence_created_count.lt.recurrence_count`)
              .or(`recurrence_end_date.is.null,recurrence_end_date.gte.${fromDateString(new Date().toISOString())}`);
            break;
          case 'completed':
            query = query.gt('recurrence_created_count', 'recurrence_count');
            break;
          case 'paused':
            // For now, paused can be indicated by a flag or specific end date
            // This can be extended based on requirements
            break;
        }
      }

      if (filters?.category_id) {
        query = query.eq('category_id', filters.category_id);
      }

      if (filters?.date_range) {
        query = query
          .gte('date', fromDateString(filters.date_range.start))
          .lte('date', fromDateString(filters.date_range.end));
      }

      const { data, error } = await query
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      // Transform and enrich data
      const enrichedData = await Promise.all(
        data.map(async (transaction) => {
          const nextDate = await calculateNextOccurrence(transaction.id);
          const childrenCount = await countChildrenTransactions(transaction.id);

          return {
            ...transaction,
            date: fromDateString(transaction.date),
            due_date: transaction.due_date ? fromDateString(transaction.due_date) : null,
            payment_date: transaction.payment_date ? fromDateString(transaction.payment_date) : null,
            recurrence_end_date: transaction.recurrence_end_date
              ? fromDateString(transaction.recurrence_end_date)
              : null,
            next_occurrence: nextDate,
            children_count: childrenCount,
            category: transaction.categories?.name || 'Sem Categoria',
            client: transaction.clients?.name || transaction.client,
            supplier: transaction.suppliers?.name || transaction.supplier,
          } as RecurringTransaction;
        })
      );

      return enrichedData;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const validateRecurrenceLimits = (
    payload: Partial<RecurringTransactionFormData> & { date?: Date | string }
  ) => {
    const hasRecurrenceFields = payload.is_recurring
      || payload.recurrence_pattern
      || payload.recurrence_count !== undefined
      || payload.recurrence_end_date !== undefined;

    if (!hasRecurrenceFields) return;

    if (payload.recurrence_count !== undefined && payload.recurrence_count > MAX_RECURRENCE_COUNT) {
      throw new Error(`Por segurança, limite o número de recorrências a no máximo ${MAX_RECURRENCE_COUNT}.`);
    }

    const startDate = parseDateValue(payload.date);
    const endDate = parseDateValue(payload.recurrence_end_date);
    const pattern = payload.recurrence_pattern;
    const interval = payload.recurrence_interval || 1;
    const isOpenEnded = payload.recurrence_count === undefined || payload.recurrence_count === null;

    if (isOpenEnded && startDate && endDate && pattern) {
      const estimatedOccurrences = calculateRecurrenceCount(startDate, endDate, pattern, interval);
      if (estimatedOccurrences > MAX_ESTIMATED_OCCURRENCES) {
        throw new Error(
          `A configuração atual geraria mais de ${MAX_ESTIMATED_OCCURRENCES} lançamentos. Ajuste a data final ou aumente o intervalo.`
        );
      }
    }
  };

  const runGenerationWithLock = async (
    seriesId: string,
    options: { generateCount?: number; upToDate?: Date | string | null },
    toastId: string,
    messages: { loading: string; success: string; skipped?: string }
  ) => {
    if (generationLocks.has(seriesId)) {
      toast.info('Geração dessa recorrência já está em andamento.', {
        description: 'Aguarde a conclusão antes de solicitar novamente.'
      });
      return { generated: false };
    }

    generationLocks.add(seriesId);
    let generated = false;

    try {
      toast.loading(messages.loading, {
        id: toastId,
        description: 'Isso pode levar alguns segundos. As próximas ocorrências continuam sendo criadas automaticamente pelo cron.'
      });

      const baseGenerateCount = options.generateCount && options.generateCount > 0
        ? options.generateCount
        : undefined;
      const safeGenerateCount = baseGenerateCount
        ? Math.min(baseGenerateCount, MAX_RECURRENCE_COUNT)
        : undefined;

      if (baseGenerateCount && safeGenerateCount !== baseGenerateCount) {
        toast.info(`Limitando a geração inicial para ${MAX_RECURRENCE_COUNT} ocorrências.`, {
          description: 'O restante será criado gradualmente pelo job automático.'
        });
      }

      const generationResult = await generateRecurringTransactionsForSeries(
        seriesId,
        {
          generateCount: safeGenerateCount,
          upToDate: options.upToDate || undefined
        }
      );

      if (generationResult.successful > 0) {
        generated = true;
        const successMessage = messages.success
          || `${generationResult.successful} transação(ões) gerada(s) com sucesso!`;
        const successDescription = `Geramos um lote inicial (${generationResult.successful}). O restante continuará sendo criado pelo job automático.`;
        toast.success(
          successMessage,
          {
            id: toastId,
            description: successDescription
          }
        );
      } else if (generationResult.skipped > 0) {
        toast.info(
          messages.skipped || 'Transações já existem para as próximas datas.',
          { id: toastId }
        );
      } else {
        toast.dismiss(toastId);
      }
    } catch (error) {
      console.error('Error generating recurring transactions:', error);
      toast.warning(
        'As transações futuras serão geradas automaticamente pelo sistema.',
        {
          id: toastId,
          description: 'Erro ao gerar transações imediatamente'
        }
      );
    } finally {
      generationLocks.delete(seriesId);
    }

    return { generated };
  };

  // Create recurring transaction
  const createRecurring = useMutation({
    mutationFn: async (formData: RecurringTransactionFormData) => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Validate recurrence pattern
      if (!isValidRecurrencePattern(formData.recurrence_pattern)) {
        throw new Error('Invalid recurrence pattern');
      }

      if (formData.recurrence_interval <= 0) {
        throw new Error('Recurrence interval must be greater than 0');
      }

      validateRecurrenceLimits(formData);

      // Prepare transaction data
      const transactionData = {
        ...formData,
        user_id: user.id,
        // Convert dates
        date: formData.date.toISOString().split('T')[0],
        due_date: formData.due_date ? formData.due_date.toISOString().split('T')[0] : null,
        competence_date: formData.competence_date ? formData.competence_date.toISOString().split('T')[0] : null,
        recurrence_end_date: formData.recurrence_end_date
          ? formData.recurrence_end_date.toISOString().split('T')[0]
          : null,
        // Reset child-specific fields for parent
        parent_transaction_id: null,
        is_recurring: true,
        recurrence_created_count: 0,
        skip_recurring_generation: true,
      };

      // Use the existing transaction creation logic
      const result = await create.mutateAsync(transactionData);

      // Update the created transaction with recurrence fields
      if (result?.id) {
        const { error: updateError } = await supabase
          .from('transactions')
          .update({
            recurrence_pattern: formData.recurrence_pattern,
            recurrence_interval: formData.recurrence_interval,
            recurrence_count: formData.recurrence_count,
            recurrence_end_date: formData.recurrence_end_date
              ? formData.recurrence_end_date.toISOString().split('T')[0]
              : null,
          })
          .eq('id', result.id);

        if (updateError) {
          throw updateError;
        }
      }

      return result;
    },

    onMutate: async (newRecurring) => {
      await queryClient.cancelQueries({ queryKey: ['recurring-transactions'] });

      const previousRecurring = queryClient.getQueryData<RecurringTransaction[]>(['recurring-transactions']);

      // Create optimistic recurring transaction
      const optimisticRecurring: RecurringTransaction = {
        id: uuidv4(),
        ...newRecurring,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        recurrence_created_count: 0,
        children_count: 0,
        next_occurrence: calculateNextRecurrenceDate(
          newRecurring.date,
          newRecurring.recurrence_pattern,
          newRecurring.recurrence_interval
        ),
      } as RecurringTransaction;

      // Optimistically add to cache
      queryClient.setQueryData<RecurringTransaction[]>(['recurring-transactions'], (old = []) => [
        optimisticRecurring,
        ...old,
      ]);

      toast.loading('Criando transação recorrente...', { id: 'recurring-create' });

      return { previousRecurring };
    },

    onError: (error: any, variables, context) => {
      if (context?.previousRecurring) {
        queryClient.setQueryData(['recurring-transactions'], context.previousRecurring);
      }

      toast.error('Erro ao criar transação recorrente', {
        id: 'recurring-create',
        description: error.message || 'Tente novamente',
      });
    },

    onSuccess: async (result) => {
      queryClient.invalidateQueries({ queryKey: ['recurring-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });

      toast.success('Transação recorrente criada com sucesso!', { id: 'recurring-create' });

      // Generate future transactions for this recurring transaction
      if (result?.id) {
        try {
          validateRecurrenceLimits({
            is_recurring: true,
            recurrence_pattern: result.recurrence_pattern,
            recurrence_interval: result.recurrence_interval,
            recurrence_count: result.recurrence_count,
            recurrence_end_date: result.recurrence_end_date,
            date: result.date,
          });
        } catch (validationError: any) {
          toast.error('Configuração de recorrência muito longa', {
            id: `generate-recurring-${result.id}`,
            description: validationError.message || 'Revise a contagem ou a data final.'
          });
          return;
        }

        const { generated } = await runGenerationWithLock(
          result.id,
          {
            generateCount: result.recurrence_count || undefined,
            upToDate: result.recurrence_end_date || undefined
          },
          `generate-recurring-${result.id}`,
          {
            loading: 'Gerando próximas ocorrências...',
            success: 'Ocorrências recorrentes geradas com sucesso!',
            skipped: 'Transações já existem para as próximas datas.'
          }
        );

        if (generated) {
          // Invalidate queries again to show the newly generated transactions
          queryClient.invalidateQueries({ queryKey: ['recurring-transactions'] });
          queryClient.invalidateQueries({ queryKey: ['transactions'] });
          queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        }
      }
    },
  });

  // Update recurring transaction
  const updateRecurring = useMutation({
    mutationFn: async ({
      id,
      formData
    }: {
      id: string;
      formData: Partial<RecurringTransactionFormData>;
    }) => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const currentRecurring = recurringTransactions.find(recurring => recurring.id === id);

      validateRecurrenceLimits({
        is_recurring: currentRecurring?.is_recurring ?? formData.is_recurring,
        recurrence_pattern: formData.recurrence_pattern ?? currentRecurring?.recurrence_pattern,
        recurrence_interval: formData.recurrence_interval ?? currentRecurring?.recurrence_interval,
        recurrence_count: formData.recurrence_count ?? currentRecurring?.recurrence_count,
        recurrence_end_date: formData.recurrence_end_date ?? currentRecurring?.recurrence_end_date,
        date: formData.date ?? currentRecurring?.date,
      });

      // Build update payload
      const updatePayload: any = {
        updated_at: new Date().toISOString(),
      };

      // Update basic transaction fields
      if (formData.amount !== undefined) updatePayload.amount = formData.amount;
      if (formData.description !== undefined) updatePayload.description = formData.description;
      if (formData.category_id !== undefined) updatePayload.category_id = formData.category_id;
      if (formData.cost_center_id !== undefined) updatePayload.cost_center_id = formData.cost_center_id;
      if (formData.notes !== undefined) updatePayload.notes = formData.notes;

      // Update recurrence fields
      if (formData.recurrence_pattern !== undefined) {
        if (!isValidRecurrencePattern(formData.recurrence_pattern)) {
          throw new Error('Invalid recurrence pattern');
        }
        updatePayload.recurrence_pattern = formData.recurrence_pattern;
      }

      if (formData.recurrence_interval !== undefined) {
        if (formData.recurrence_interval <= 0) {
          throw new Error('Recurrence interval must be greater than 0');
        }
        updatePayload.recurrence_interval = formData.recurrence_interval;
      }

      if (formData.recurrence_count !== undefined) {
        updatePayload.recurrence_count = formData.recurrence_count;
      }

      if (formData.recurrence_end_date !== undefined) {
        updatePayload.recurrence_end_date = formData.recurrence_end_date
          ? formData.recurrence_end_date.toISOString().split('T')[0]
          : null;
      }

      const { error } = await supabase
        .from('transactions')
        .update(updatePayload)
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }

      return { id, formData };
    },

    onMutate: async ({ id, formData }) => {
      await queryClient.cancelQueries({ queryKey: ['recurring-transactions'] });

      const previousRecurring = queryClient.getQueryData<RecurringTransaction[]>(['recurring-transactions']);

      // Optimistically update the transaction
      queryClient.setQueryData<RecurringTransaction[]>(['recurring-transactions'], (old = []) =>
        old.map(recurring =>
          recurring.id === id
            ? {
                ...recurring,
                ...formData,
                updated_at: new Date().toISOString(),
                next_occurrence: formData.recurrence_pattern && formData.recurrence_interval
                  ? calculateNextRecurrenceDate(
                      recurring.date,
                      formData.recurrence_pattern,
                      formData.recurrence_interval
                    )
                  : recurring.next_occurrence,
              }
            : recurring
        )
      );

      toast.loading('Atualizando transação recorrente...', { id: 'recurring-update' });

      return { previousRecurring };
    },

    onError: (error: any, variables, context) => {
      if (context?.previousRecurring) {
        queryClient.setQueryData(['recurring-transactions'], context.previousRecurring);
      }

      toast.error('Erro ao atualizar transação recorrente', {
        id: 'recurring-update',
        description: error.message || 'Tente novamente',
      });
    },

    onSuccess: async ({ id, formData }) => {
      queryClient.invalidateQueries({ queryKey: ['recurring-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });

      toast.success('Transação recorrente atualizada com sucesso!', { id: 'recurring-update' });

      // Check if recurrence fields were modified
      const recurrenceFieldsChanged = [
        'recurrence_pattern',
        'recurrence_interval',
        'recurrence_count',
        'recurrence_end_date'
      ].some(field => formData?.[field] !== undefined);

      // If recurrence fields changed, regenerate future transactions
      if (recurrenceFieldsChanged && id) {
        const updatedRecurring = recurringTransactions.find(recurring => recurring.id === id);

        try {
          validateRecurrenceLimits({
            is_recurring: updatedRecurring?.is_recurring ?? true,
            recurrence_pattern: formData.recurrence_pattern ?? updatedRecurring?.recurrence_pattern,
            recurrence_interval: formData.recurrence_interval ?? updatedRecurring?.recurrence_interval,
            recurrence_count: formData.recurrence_count ?? updatedRecurring?.recurrence_count,
            recurrence_end_date: formData.recurrence_end_date ?? updatedRecurring?.recurrence_end_date,
            date: formData.date ?? updatedRecurring?.date,
          });
        } catch (validationError: any) {
          toast.error('Configuração de recorrência muito longa', {
            id: `regenerate-recurring-${id}`,
            description: validationError.message || 'Revise a contagem ou a data final.'
          });
          return;
        }

        const { generated } = await runGenerationWithLock(
          id,
          {
            generateCount: formData.recurrence_count ?? updatedRecurring?.recurrence_count,
            upToDate: formData.recurrence_end_date ?? updatedRecurring?.recurrence_end_date
          },
          `regenerate-recurring-${id}`,
          {
            loading: 'Regenerando transações futuras...',
            success: 'Ocorrências recorrentes atualizadas com sucesso!',
            skipped: 'Transações futuras já estão atualizadas.'
          }
        );

        if (generated) {
          // Invalidate queries again to show the regenerated transactions
          queryClient.invalidateQueries({ queryKey: ['recurring-transactions'] });
          queryClient.invalidateQueries({ queryKey: ['transactions'] });
        }
      }
    },
  });

  // Pause recurring transaction (by setting end date to today)
  const pauseRecurring = useMutation({
    mutationFn: async (id: string) => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase
        .from('transactions')
        .update({
          recurrence_end_date: new Date().toISOString().split('T')[0],
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }

      return id;
    },

    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['recurring-transactions'] });

      const previousRecurring = queryClient.getQueryData<RecurringTransaction[]>(['recurring-transactions']);

      // Optimistically update
      queryClient.setQueryData<RecurringTransaction[]>(['recurring-transactions'], (old = []) =>
        old.map(recurring =>
          recurring.id === id
            ? {
                ...recurring,
                recurrence_end_date: new Date(),
                updated_at: new Date().toISOString(),
              }
            : recurring
        )
      );

      toast.loading('Pausando transação recorrente...', { id: 'recurring-pause' });

      return { previousRecurring };
    },

    onError: (error: any, variables, context) => {
      if (context?.previousRecurring) {
        queryClient.setQueryData(['recurring-transactions'], context.previousRecurring);
      }

      toast.error('Erro ao pausar transação recorrente', {
        id: 'recurring-pause',
        description: error.message || 'Tente novamente',
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-transactions'] });

      toast.success('Transação recorrente pausada com sucesso!', { id: 'recurring-pause' });
    },
  });

  // Resume recurring transaction (clear end date)
  const resumeRecurring = useMutation({
    mutationFn: async (id: string) => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase
        .from('transactions')
        .update({
          recurrence_end_date: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }

      return id;
    },

    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['recurring-transactions'] });

      const previousRecurring = queryClient.getQueryData<RecurringTransaction[]>(['recurring-transactions']);

      // Optimistically update
      queryClient.setQueryData<RecurringTransaction[]>(['recurring-transactions'], (old = []) =>
        old.map(recurring =>
          recurring.id === id
            ? {
                ...recurring,
                recurrence_end_date: null,
                updated_at: new Date().toISOString(),
              }
            : recurring
        )
      );

      toast.loading('Retomando transação recorrente...', { id: 'recurring-resume' });

      return { previousRecurring };
    },

    onError: (error: any, variables, context) => {
      if (context?.previousRecurring) {
        queryClient.setQueryData(['recurring-transactions'], context.previousRecurring);
      }

      toast.error('Erro ao retomar transação recorrente', {
        id: 'recurring-resume',
        description: error.message || 'Tente novamente',
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-transactions'] });

      toast.success('Transação recorrente retomada com sucesso!', { id: 'recurring-resume' });
    },
  });

  // Delete recurring transaction and all its children
  const deleteRecurring = useMutation({
    mutationFn: async (id: string) => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // First, delete all child transactions
      const { error: childrenError } = await supabase
        .from('transactions')
        .delete()
        .eq('parent_transaction_id', id)
        .eq('user_id', user.id);

      if (childrenError) {
        throw childrenError;
      }

      // Then delete the parent
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }

      return id;
    },

    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['recurring-transactions'] });
      await queryClient.cancelQueries({ queryKey: ['transactions'] });

      const previousRecurring = queryClient.getQueryData<RecurringTransaction[]>(['recurring-transactions']);
      const previousTransactions = queryClient.getQueryData<Transaction[]>(['transactions']);

      // Optimistically remove from caches
      queryClient.setQueryData<RecurringTransaction[]>(['recurring-transactions'], (old = []) =>
        old.filter(recurring => recurring.id !== id)
      );

      queryClient.setQueryData<Transaction[]>(['transactions'], (old = []) =>
        old.filter(transaction => transaction.id !== id && transaction.parent_transaction_id !== id)
      );

      toast.loading('Excluindo transação recorrente...', { id: 'recurring-delete' });

      return { previousRecurring, previousTransactions };
    },

    onError: (error: any, variables, context) => {
      if (context?.previousRecurring) {
        queryClient.setQueryData(['recurring-transactions'], context.previousRecurring);
      }
      if (context?.previousTransactions) {
        queryClient.setQueryData(['transactions'], context.previousTransactions);
      }

      toast.error('Erro ao excluir transação recorrente', {
        id: 'recurring-delete',
        description: error.message || 'Tente novamente',
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });

      toast.success('Transação recorrente excluída com sucesso!', { id: 'recurring-delete' });
    },
  });

  // Helper functions
  const calculateNextOccurrence = async (transactionId: string): Promise<Date | null> => {
    try {
      const { data, error } = await supabase
        .rpc('calculate_next_recurrence_date_for_transaction', {
          p_current_date: new Date().toISOString().split('T')[0],
          p_transaction_id: transactionId,
        });

      if (error) {
        console.error('Error calculating next occurrence:', error);
        return null;
      }

      return data ? fromDateString(data) : null;
    } catch (error) {
      console.error('Error in calculateNextOccurrence:', error);
      return null;
    }
  };

  const countChildrenTransactions = async (parentTransactionId: string): Promise<number> => {
    try {
      const { count, error } = await supabase
        .from('transactions')
        .select('*', { count: 'exact', head: true })
        .eq('parent_transaction_id', parentTransactionId);

      if (error) {
        console.error('Error counting children:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error in countChildrenTransactions:', error);
      return 0;
    }
  };

  // Real-time subscription for recurring transactions
  useEffect(() => {
    const channel = supabase
      .channel(`recurring-transactions-changes_${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transactions',
          filter: 'is_recurring=eq.true',
        },
        (payload) => {
          // Invalidate cache on any change to recurring transactions
          queryClient.invalidateQueries({ queryKey: ['recurring-transactions'] });
          queryClient.invalidateQueries({ queryKey: ['transactions'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  // Generate preview of next occurrences
  const generatePreview = useCallback((startDate: Date, pattern: RecurrencePattern, interval: number, count?: number, endDate?: Date) => {
    const dates: Date[] = [];
    let currentDate = new Date(startDate);

    if (count) {
      for (let i = 0; i < count && i < 10; i++) { // Limit preview to 10 dates
        dates.push(new Date(currentDate));
        currentDate = calculateNextRecurrenceDate(currentDate, pattern, interval);
      }
    } else if (endDate) {
      while (currentDate <= endDate && dates.length < 10) {
        dates.push(new Date(currentDate));
        currentDate = calculateNextRecurrenceDate(currentDate, pattern, interval);
      }
    }

    return dates;
  }, []);

  // Get recurrence description for display
  const getRecurrenceDescription = useCallback((transaction: RecurringTransaction) => {
    return generateRecurrenceDescription(
      transaction.recurrence_pattern,
      transaction.recurrence_interval,
      transaction.recurrence_count,
      transaction.recurrence_end_date as Date | null
    );
  }, []);

  return {
    // Data
    recurringTransactions,
    isLoading,
    error,

    // Mutations
    createRecurring,
    updateRecurring,
    pauseRecurring,
    resumeRecurring,
    deleteRecurring,

    // States
    isCreating: createRecurring.isPending,
    isUpdating: updateRecurring.isPending,
    isPausing: pauseRecurring.isPending,
    isResuming: resumeRecurring.isPending,
    isDeleting: deleteRecurring.isPending,

    // Helper functions
    generatePreview,
    getRecurrenceDescription,
    calculateNextOccurrence,

    // Computed values
    activeRecurringCount: recurringTransactions.filter(r =>
      !r.recurrence_end_date || new Date(r.recurrence_end_date) >= new Date()
    ).length,

    monthlyTotal: recurringTransactions
      .filter(r => r.recurrence_pattern === 'monthly')
      .reduce((sum, r) => sum + r.amount, 0),
  };
}
